import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Flex, useSafeArea } from '@banhala/ui-kit';
import type { OutfitEntity, TwoColOutfitListProps } from './types';
import { OutfitCard } from './OutfitCard';
import { useLogDisplayContent } from './log/useLogDisplayContent';
import { outfitService } from '../../../../api';
import { bridge } from '../../../../utils';
import { useMyInfo } from '../../../../queries';
import { useBottomDockedHeight, useOpenDeeplink } from '../../../../hooks';
import { useAtomicScreen } from '../../context/AtomicScreenContext';
import { bookmarkOutfitToast } from '../../../Toast/bookmarkOutfitToast';

// 컨테이너 paddingHorizontal(11.5) + 카드 paddingHorizontal(3.5) = 15px 스크린 엣지
// 카드 간 갭: 3.5 + 3.5 = 7px
const CONTAINER_PADDING = 11.5;

// iOS failable init 동일: 필수 필드 누락 아이템 필터링
const isValidEntity = (e: OutfitEntity): boolean =>
  Boolean(e.item?.thumbnail && e.item?.deeplink && e.item?.sno);

export const TwoColOutfitList = ({
  entity,
  bookmarkMap,
  setBookmarkMap,
}: TwoColOutfitListProps) => {
  const { data: memberInfo, refetch: refetchMemberInfo } = useMyInfo();
  const displayLoggedRef = useRef(false);
  const { logDisplayContent } = useLogDisplayContent();
  const { bottom } = useSafeArea();
  const bottomDockedHeight = useBottomDockedHeight();
  const { openDeeplink } = useOpenDeeplink();
  const screen = useAtomicScreen() ?? '';

  // iOS compactMap 동일: 유효한 아이템만 렌더링
  const validEntity = useMemo(
    () => entity?.filter(isValidEntity) ?? null,
    [entity],
  );

  // API 응답에서 초기 bookmarkMap 생성 (TwoColGoodsList likeMap 초기화 동일 패턴)
  useEffect(() => {
    if (!validEntity) return;
    setBookmarkMap(prev => ({
      ...prev,
      ...validEntity.reduce<Record<string, boolean>>((acc, cur) => {
        acc[cur.item.sno] = cur.item.bookmarked;
        return acc;
      }, {}),
    }));
  }, [validEntity, setBookmarkMap]);

  // 마운트 시 DISPLAY_CONTENT 로깅 (iOS DisplayTrackableView 동일)
  useEffect(() => {
    if (!validEntity || displayLoggedRef.current) return;
    displayLoggedRef.current = true;
    logDisplayContent({
      viewableItems: validEntity.map((item, index) => ({
        item,
        index,
        isViewable: true,
        key: item.item.sno.toString(),
      })),
      changed: [],
    });
  }, [validEntity, logDisplayContent]);

  const handleToggleBookmark = useCallback(
    async (sno: number, nextBookmarked: boolean) => {
      // 로그인 확인 (useAddLikeGoods 동일 패턴: 비회원이면 refetch 후 재확인)
      if (!memberInfo || memberInfo.is_anonymous) {
        const refreshed = await refetchMemberInfo().then(r => r.data);
        if (!refreshed || refreshed.is_anonymous) {
          bridge.assertLogin({});
          return;
        }
      }

      // 낙관적 UI 업데이트
      setBookmarkMap(prev => ({ ...prev, [sno]: nextBookmarked }));

      try {
        if (nextBookmarked) {
          // iOS MetaFeedOutfitCell.bookmark / Android OutfitBookmarkUseCase.performBookmark 동일:
          // 추가 시에만 응답의 deeplink로 '저장한 코디 보러가기' 토스트 노출
          const { deeplink } = await outfitService.postBookmark(sno);
          bookmarkOutfitToast({
            deeplink,
            screen,
            bottomOffset: bottom + bottomDockedHeight + 12,
            openDeeplink,
          });
        } else {
          await outfitService.removeBookmark(sno);
        }
        // 네이티브 캐시 동기화
        bridge.bookmarkOutfit({ outfit_sno: sno, is_bookmarked: nextBookmarked });
      } catch {
        // 실패 시 롤백
        setBookmarkMap(prev => ({ ...prev, [sno]: !nextBookmarked }));
      }
    },
    [
      memberInfo,
      refetchMemberInfo,
      setBookmarkMap,
      screen,
      bottom,
      bottomDockedHeight,
      openDeeplink,
    ],
  );

  if (!validEntity || validEntity.length === 0) {
    return null;
  }

  return (
    <Flex.Row flexWrap="wrap" padding={{ horizontal: CONTAINER_PADDING }}>
      {validEntity.map(e => (
        <OutfitCard
          key={e.item.sno}
          entity={e}
          isBookmarked={bookmarkMap[e.item.sno] ?? e.item.bookmarked}
          onToggleBookmark={handleToggleBookmark}
        />
      ))}
    </Flex.Row>
  );
};
